import React from 'react';
import { Severity } from '../types';
import { SEVERITY_TONE } from '../styles';
import Tag from '../../components/ui/Tag';
import Panel from '../../components/ui/Panel';
import { DataTable } from '../../components/table/DataTable';
import type { ColumnDef } from '@tanstack/react-table';
import { PANEL_BADGE } from './panelChrome';

interface SummaryRow {
  priority: string;
  finding: string;
  action: string;
}

interface SummaryTableProps {
  rows: SummaryRow[];
}

const priorityToSeverity = (p: string): Severity => {
  const lower = p.toLowerCase();
  if (lower.includes('high') || lower.includes('critical') || lower.includes("\u{1F534}")) return 'critical';
  if (lower.includes('medium') || lower.includes('warning') || lower.includes("\u{1F7E0}")) return 'warning';
  if (lower.includes('low') || lower.includes("\u{1F7E1}")) return 'advisory';
  if (lower.includes('ok') || lower.includes("✅")) return 'info';
  return 'info';
};

const COLUMNS: ColumnDef<SummaryRow, any>[] = [
  {
    id: 'priority',
    header: 'Priority',
    meta: { cellClass: 'whitespace-nowrap align-top' },
    cell: ({ row }) => (
      <Tag tone={SEVERITY_TONE[priorityToSeverity(row.original.priority)]}>
        {row.original.priority.replace(/[^\w\s/]/g, '').trim()}
      </Tag>
    ),
  },
  {
    id: 'finding',
    header: 'Finding',
    meta: { cellClass: 'min-w-[12rem] align-top text-on-surface' },
    cell: ({ row }) => row.original.finding,
  },
  {
    id: 'action',
    header: 'Action',
    meta: { cellClass: 'min-w-[12rem] align-top text-on-surface-variant' },
    cell: ({ row }) => row.original.action,
  },
];

const SummaryTable: React.FC<SummaryTableProps> = ({ rows }) => {
  if (!rows || rows.length === 0) return null;

  return (
    <Panel
      level={3}
      title="Summary"
      actions={<span className={PANEL_BADGE}>{rows.length} items</span>}
      padding="none"
      className="overflow-hidden"
    >
      <DataTable<SummaryRow>
        data={rows}
        columns={COLUMNS}
        getRowId={(_, i) => String(i)}
        on="panel"
        density="compact"
        striped
        empty={null}
      />
    </Panel>
  );
};

export default SummaryTable;
